import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './DesktopModelsContextMenu.css';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function DesktopModelsContextMenu({
  anchorX,
  anchorY,
  model,
  onClose,
  onToggleActive,
}) {
  const overlayRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({ x: anchorX, y: anchorY });

  const fileMissing = !!model?.fileMissing;
  const isActive = !!model?.isActive;
  const toggleLabel = isActive ? 'Deactivate Model' : 'Activate Model';

  const disabled = fileMissing;
  const toggleAction = () => onToggleActive?.(model);

  const iconClass = useMemo(() => {
    if (disabled) return 'fa-solid fa-lock';
    return isActive ? 'fa-solid fa-toggle-off' : 'fa-solid fa-toggle-on';
  }, [disabled, isActive]);

  // Keep the menu near the click point and inside the visible viewport.
  useLayoutEffect(() => {
    const padding = 12;
    const overlayRect = overlayRef.current?.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || 260;
    const menuHeight = menuRef.current?.offsetHeight || 140;

    const baseX = (anchorX ?? 0) - (overlayRect?.left ?? 0);
    const baseY = (anchorY ?? 0) - (overlayRect?.top ?? 0);

    const maxX = Math.max(padding, (overlayRect?.width ?? window.innerWidth) - menuWidth - padding);
    const maxY = Math.max(padding, (overlayRect?.height ?? window.innerHeight) - menuHeight - padding);

    const safeX = clamp(baseX, padding, maxX);
    const safeY = clamp(baseY, padding, maxY);

    setCoords({ x: safeX, y: safeY });
  }, [anchorX, anchorY]);

  // Close on escape or scroll.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    const handleScroll = () => onClose?.();

    window.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  if (!model) return null;

  return (
    <div className="desktop-models-context-menu-overlay" onClick={onClose} ref={overlayRef}>
      <div
        className="desktop-models-context-menu"
        style={{ left: coords.x, top: coords.y }}
        role="menu"
        aria-label="Model actions"
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="desktop-models-context-menu__title">{model.displayName || 'Model'}</div>
        <div className="desktop-models-context-menu__item-row">
          <button
            type="button"
            className={`desktop-models-context-menu__item ${disabled ? 'is-disabled' : ''}`}
            role="menuitem"
            onClick={() => {
              if (disabled) return;
              toggleAction();
              onClose?.();
            }}
            disabled={disabled}
            title={disabled ? 'Model file is missing' : toggleLabel}
          >
            <i className={iconClass} />
            <span>{toggleLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

